import React, {useCallback, useMemo, useState} from "react";
import useAccessLevels from "../../../../hooks/use-access-levels";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {OrderDimensionsDataGridColumnNames} from "../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../packages/data-grid";
import {Box, Button, CircularProgress, MenuItem, Skeleton, SvgIcon, TextField, Tooltip, Typography} from "@mui/material";
import ProductInformationPopover from "../../draggable-popover/product-information-popover";
import PartnerDataGrid from "../../../../containers/data-grid";
import ManageProductDimensionsDialog from "../../../dialogs/manage-product-dimensions";
import {ProductDimensionUnit, useSelectedDimensionMetrics} from "../../manage-product-dimensions/unit-management";
import Api from "../../../../../core/services/api/api";
import {Alert} from "@mui/lab";
import IconButton from "../../../base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../assets/images/remove.svg";
import {setReduxDialogState} from "../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../core/models/static/routes/redux-store-routes";
import {useAppDispatch} from "../../../../redux";
import {getRowKey} from "./dimensions-utils";

const dataGridSaveConfig = {
    name: `OrderDimensionsPartsManagementDataGrid`,
    version: 2,
}

const initManageProductDimensionsDialogState = {open: false, partNo: null, partId: null, close: () => void 0, onDimensionSelected: undefined,}

export const OrderDimensionsSectionPartsManagement = ({
                                                          orderParts,
                                                          mergedParts: mergedPartsProp,
                                                          containers,
                                                          setOrderDimensions,
                                                          loading,
                                                          currentContainerTypeTitle,
                                                          getContainerTitle,
                                                          orderId,
                                                          invoiceId,
                                                      }) => {
    const [dimensions, setDimensions] = useState({});
    const [loadingDimensionOptions, setLoadingDimensionOptions] = useState([]);

    const {lengthUnit, weightUnit, transformLengthValue, transformWeightValue} = useSelectedDimensionMetrics();
    const [settingContainerKeys, setSettingContainerKeys] = useState([]);
    const [removingKeys, setRemovingKeys] = useState([]);
    const [settingDimensionKeys, setSettingDimensionKeys] = useState([]);
    const operatorAccessLevels = useAccessLevels();
    const [manageProductDimensionsDialogState, setManageProductDimensionsDialogState] = useState(initManageProductDimensionsDialogState);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();

    /**
     * Load dimensions for a part
     * @type {(function(*): Promise<*>)}
     */
    const loadDimensionsForPart = useCallback(async (mergedPart) => {
        setLoadingDimensionOptions(prev => ([...prev, getRowKey(mergedPart)]));
        const response = await Api.getPartSizesList(mergedPart.partId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const data = response.data ?? [];
            setDimensions(prevState => {
                const newState = {...prevState};
                newState[mergedPart.partId] = data;
                return newState;
            });
        }
        setLoadingDimensionOptions(prev => prev.filter(k => k !== getRowKey(mergedPart)));
    }, [isMounted])

    const mergedParts = useMemo(
        () => mergedPartsProp?.map(part => ({
            ...part,
            availableDimensions: part.partId ? dimensions[part.partId] : undefined,
        })) ?? [],
        [mergedPartsProp, dimensions],
    );

    /**
     * Memo version of column in the data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.sales.manageSalesPallets ? [
                {
                    name: OrderDimensionsDataGridColumnNames.clear,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    resizable: false,
                    visibilityToggleable: false,
                    pinnedToggleable: false,
                    reorderable: false,
                    sortable: false,
                    pinned: true,
                    width: 0,
                },
            ] : []),
            {
                title: currentContainerTypeTitle,
                name: OrderDimensionsDataGridColumnNames.container,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                width: 120,
            },
            {
                title: "Part No",
                name: OrderDimensionsDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 130,
            },
            {
                title: "Serial No",
                name: OrderDimensionsDataGridColumnNames.serialNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Qty",
                name: OrderDimensionsDataGridColumnNames.qty,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 80,
            },
            {
                title: "Length",
                name: OrderDimensionsDataGridColumnNames.length,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 80,
            },
            {
                title: "Width",
                name: OrderDimensionsDataGridColumnNames.width,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 80,
            },
            {
                title: "Height",
                name: OrderDimensionsDataGridColumnNames.height,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 80,
            },
            {
                title: "Weight",
                name: OrderDimensionsDataGridColumnNames.weight,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 80,
            },
            {
                title: "Volume",
                name: OrderDimensionsDataGridColumnNames.volume,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Vendor",
                name: OrderDimensionsDataGridColumnNames.vendorCode,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            // {
            //     title: "Comment",
            //     name: OrderDimensionsDataGridColumnNames.comment,
            //     alignment: DataGridColumnAlignments.left,
            //     type: DataGridColumnTypes.string,
            //     sortable: false,
            //     width: 250,
            // },
            {
                title: "Notes",
                name: OrderDimensionsDataGridColumnNames.description,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 250,
            },
            ...(operatorAccessLevels.packages.sales.manageSalesPallets || operatorAccessLevels.packages.lookup.editProductDimensions ? [
                {
                    name: OrderDimensionsDataGridColumnNames.sizeOptions,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 120,
                    pinned: true,
                    pinnedType: DataGridColumnPinnedTypes.right,
                },
            ] : []),
        ], [currentContainerTypeTitle, operatorAccessLevels.packages.lookup.editProductDimensions, operatorAccessLevels.packages.sales.manageSalesPallets]);


    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Set the container for a part
     * @type {(function(*, *): void)}
     */
    const setContainerForPart = useCallback(async (mergedPart, palletId) => {
        const key = getRowKey(mergedPart);
        setSettingContainerKeys(prev => ([...prev, key]));
        const forApi = {
            id: mergedPart.dimensionInfo?.id,
            orderPartId: mergedPart.orderPartId,
            serialNumber: mergedPart.serial,
            qty: mergedPart.qty,
            height: mergedPart.dimensionInfo?.height || 0,
            width: mergedPart.dimensionInfo?.width || 0,
            length: mergedPart.dimensionInfo?.length || 0,
            weight: mergedPart.dimensionInfo?.weight || 0,
            notes: mergedPart.dimensionInfo?.notes || "",
            partId: mergedPart.partId,
            vendorId: mergedPart.dimensionInfo?.vendorId || null,
            orderId: orderId,
            invoiceId: invoiceId,
            orderPalletId: Number(palletId),
            updatedProperties: ["OrderPalletId", "OrderPartId", "SerialNumber", "Qty", "OrderId", "InvoiceId"],
        }
        const updating = !!mergedPart.dimensionInfo?.id;
        const response = updating ? await Api.updateOrderDimension(forApi) : await Api.createOrderDimension(forApi);
        if (!isMounted())
            return;
        if (response.resultFlag) {
            setOrderDimensions(prev => {
                const updatedProperties = {
                    ...(response.data ?? {}),
                    orderPalletId: Number(palletId),
                    orderPalletTitle: getContainerTitle(containers.find(c => c.id === Number(palletId))) || "",
                }
                if (updating) {
                    return prev.map(e => e.id === mergedPart.dimensionInfo?.id ? {...e, ...updatedProperties} : e);
                } else {
                    return ([...prev, updatedProperties]);
                }
            })
        }
        setSettingContainerKeys(prev => prev.filter(k => k !== key));
    }, [setOrderDimensions, orderId, invoiceId, isMounted, getContainerTitle, containers]);

    /**
     * Set the dimension for a part
     * @type {(function(*, *): void)}
     */
    const setDimensionForPart = useCallback(async (mergedPart, dimension) => {
        const key = getRowKey(mergedPart);
        setSettingDimensionKeys(prev => ([...prev, key]));
        const forApi = {
            id: mergedPart.dimensionInfo?.id,
            orderPartId: mergedPart.orderPartId,
            serialNumber: mergedPart.serial,
            qty: mergedPart.qty,
            height: dimension.height,
            width: dimension.width,
            length: dimension.length,
            weight: dimension.weight,
            notes: dimension.comment || "",
            vendorId: dimension.vendorId || null,
            partId: mergedPart.partId,
            orderId: orderId,
            invoiceId: invoiceId,
            orderPalletId: mergedPart.dimensionInfo?.orderPalletId || null,
            updatedProperties: ["Height", "Width", "Length", "Weight", "Notes", "VendorId", "OrderPartId", "SerialNumber", "Qty", "OrderId", "InvoiceId"],
        }
        const updating = !!mergedPart.dimensionInfo?.id;
        const response = updating ? await Api.updateOrderDimension(forApi) : await Api.createOrderDimension(forApi);
        if (!isMounted())
            return;
        if (response.resultFlag) {
            setOrderDimensions(prev => {
                const updatedProperties = {
                    ...(response.data ?? {}),
                    height: dimension.height,
                    width: dimension.width,
                    length: dimension.length,
                    weight: dimension.weight,
                    notes: dimension.comment,
                    vendorId: dimension.vendorId,
                }
                if (updating) {
                    return prev.map(e => e.id === mergedPart.dimensionInfo?.id ? {...e, ...updatedProperties} : e);
                } else {
                    return ([...prev, updatedProperties]);
                }
            })
        }
        setSettingDimensionKeys(prev => prev.filter(k => k !== key));
    }, [invoiceId, isMounted, orderId, setOrderDimensions]);

    /**
     * Removes an order dimension from the order.
     * @type {(function(*))}
     */
    const removeOrderDimension = useCallback(async (mergedPart) => {
        if (!operatorAccessLevels.packages.sales.manageSalesPallets) {
            return;
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `the dimension information from the committed ${mergedPart.serial ? "Serial No. " : "Part"}${mergedPart.serial || mergedPart.partNo}`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return true;
                setRemovingKeys(prev => ([...prev, getRowKey(mergedPart)]));
                const forApi = {
                    orderId: orderId,
                    invoiceId: invoiceId,
                    orderPartId: mergedPart.orderPartId,
                    serialNumber: mergedPart.serial,
                }
                const response = await Api.deleteOrderDimension(forApi);
                if (!isMounted())
                    return;
                if (response?.resultFlag) {
                    setOrderDimensions(prev => prev.filter(e => e.id !== mergedPart.dimensionInfo?.id));
                }
                setRemovingKeys(prev => prev.filter(k => k !== getRowKey(mergedPart)));
                return response?.resultFlag;
            }
        }));
    }, [dispatch, invoiceId, isMounted, orderId, setOrderDimensions, operatorAccessLevels.packages.sales.manageSalesPallets])

    /**
     * Open the dimension management dialog
     * @type {(function(*))}
     */
    const openDimensionManagement = useCallback((mergedPart) => {
        setManageProductDimensionsDialogState({
            open: true,
            partNo: mergedPart.partNo,
            partId: mergedPart.partId,
            close: ({dimensions, loadingState} = {}) => {
                if (!loadingState) {
                    setDimensions(prevState => {
                        const newState = {...prevState};
                        newState[mergedPart.partId] = dimensions;
                        return newState;
                    });
                }
                setManageProductDimensionsDialogState(initManageProductDimensionsDialogState)
            },
            onDimensionSelected: (dimension) => setDimensionForPart(mergedPart, dimension),
        });
    }, [setDimensionForPart, setDimensions]);

    const dataGridRows = useMemo(() => {
        return mergedParts?.map(part => {
            const key = getRowKey(part);
            const isSettingContainer = settingContainerKeys.includes(key);
            const isSettingDimension = settingDimensionKeys.includes(key);
            const isLoadingDimensions = loadingDimensionOptions.includes(key);
            const isRemoving = removingKeys.includes(key);

            const container = part.dimensionInfo?.orderPalletId && containers.find(container => container.id === part.dimensionInfo?.orderPalletId);

            return ({
                key: key,
                data: part,
                cells: {
                    [OrderDimensionsDataGridColumnNames.clear]:
                        <>
                            {
                                operatorAccessLevels.packages.sales.manageSalesPallets &&
                                !!part.dimensionInfo?.id &&
                                <Tooltip title={`Remove dimension information from the committed ${part.serial ? "Serial No. " : "Part"}`}>
                                    <Box component={'span'}>
                                        <IconButton
                                            onPressed={() => removeOrderDimension(part)}
                                            disabled={isRemoving || isSettingDimension || isSettingContainer || loading}
                                            color={'paper.error'}
                                            className={'remove-icon'}
                                        >
                                            {
                                                isRemoving
                                                    ? <CircularProgress size={20} color={'secondary'}/>
                                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                                            }
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            }
                        </>,
                    [OrderDimensionsDataGridColumnNames.container]:
                        operatorAccessLevels.packages.sales.manageSalesPallets
                            ? <TextField
                                select
                                fullWidth
                                size={'small'}
                                value={part.dimensionInfo?.orderPalletId || ""}
                                disabled={isSettingContainer || isRemoving || containers.length === 0 || loading}
                                onChange={(e) => setContainerForPart(part, e.target.value)}
                                sx={{textAlign: 'start'}}
                                SelectProps={{
                                    renderValue: selected => {
                                        if (selected)
                                            return getContainerTitle(containers.find(container => container.id === Number(selected)), true) ?? "";
                                        return `Select ${currentContainerTypeTitle}`;
                                    },
                                    displayEmpty: true,
                                }}
                            >
                                {
                                    containers.map(container => (
                                        <MenuItem key={container.id} value={container.id}>
                                            {getContainerTitle(container, true)}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                            : getContainerTitle(container, true) ?? "N/A",
                    [OrderDimensionsDataGridColumnNames.partNo]:
                        <ProductInformationPopover partNo={part.partNo}>
                            {part.partNo ?? ""}
                        </ProductInformationPopover>,
                    [OrderDimensionsDataGridColumnNames.serialNo]: part.serial,
                    [OrderDimensionsDataGridColumnNames.qty]: !!part.serial ? 1 : part.qty,
                    ...(!(isLoadingDimensions || isSettingDimension) && {
                        [OrderDimensionsDataGridColumnNames.height]:
                            <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                {
                                    !!part.dimensionInfo?.height
                                        ? <>{transformLengthValue(part.dimensionInfo.height, {fromDefault: true})} <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/></>
                                        : ' '
                                }
                            </Typography>,
                        [OrderDimensionsDataGridColumnNames.width]:
                            <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                {
                                    !!part.dimensionInfo?.width
                                        ? <>{transformLengthValue(part.dimensionInfo.width, {fromDefault: true})} <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/></>
                                        : ' '
                                }
                            </Typography>,
                        [OrderDimensionsDataGridColumnNames.length]:
                            <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                {
                                    !!part.dimensionInfo?.length
                                        ? <>{transformLengthValue(part.dimensionInfo.length, {fromDefault: true})} <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/></>
                                        : ' '
                                }
                            </Typography>,
                        [OrderDimensionsDataGridColumnNames.volume]:
                            <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                {
                                    !!part.dimensionInfo?.length && !!part.dimensionInfo?.width && !!part.dimensionInfo?.height
                                        ? <>
                                            {transformLengthValue(part.dimensionInfo.length * part.dimensionInfo.width * part.dimensionInfo.height, {fromDefault: true})}
                                            <ProductDimensionUnit unit={`${lengthUnit}³`} backgroundColor={"transparent"}/></>
                                        : ' '
                                }
                            </Typography>,
                        [OrderDimensionsDataGridColumnNames.weight]:
                            <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                {
                                    !!part.dimensionInfo?.weight
                                        ? <>{transformWeightValue(part.dimensionInfo.weight, {fromDefault: true})} <ProductDimensionUnit unit={weightUnit} backgroundColor={"transparent"}/></>
                                        : ' '
                                }
                            </Typography>,
                        [OrderDimensionsDataGridColumnNames.comment]: part.dimensionInfo?.comment,
                        [OrderDimensionsDataGridColumnNames.vendorCode]: part.dimensionInfo?.id ? part.dimensionInfo?.vendorCode ?? "General" : " ",
                    }),
                    ...((isLoadingDimensions || isSettingDimension) && {
                        [OrderDimensionsDataGridColumnNames.height]: (isLoadingDimensions || isSettingDimension)
                            ? <Skeleton width={60} height={20} sx={{transform: 'none'}}/>
                            : " ",
                        [OrderDimensionsDataGridColumnNames.width]: (isLoadingDimensions || isSettingDimension)
                            ? <Skeleton width={60} height={20} sx={{transform: 'none'}}/>
                            : " ",
                        [OrderDimensionsDataGridColumnNames.length]: (isLoadingDimensions || isSettingDimension)
                            ? <Skeleton width={60} height={20} sx={{transform: 'none'}}/>
                            : ' ',
                        [OrderDimensionsDataGridColumnNames.volume]: (isLoadingDimensions || isSettingDimension)
                            ? <Skeleton width={60} height={20} sx={{transform: 'none'}}/>
                            : ' ',
                        [OrderDimensionsDataGridColumnNames.weight]: (isLoadingDimensions || isSettingDimension)
                            ? <Skeleton width={60} height={20} sx={{transform: 'none'}}/>
                            : ' ',
                        [OrderDimensionsDataGridColumnNames.comment]: (isLoadingDimensions || isSettingDimension)
                            ? <Skeleton width={100} height={20} sx={{transform: 'none'}}/>
                            : ' ',
                        [OrderDimensionsDataGridColumnNames.vendorCode]: (isLoadingDimensions || isSettingDimension)
                            ? <Skeleton width={80} height={20} sx={{transform: 'none'}}/>
                            : ' ',
                    }),
                    [OrderDimensionsDataGridColumnNames.description]: part.description,
                    [OrderDimensionsDataGridColumnNames.sizeOptions]:
                        <Box sx={{display: 'flex', gap: 1, alignItems: 'center', width: 'fit-content'}}>
                            {
                                operatorAccessLevels.packages.sales.manageSalesPallets &&
                                <TextField
                                    select
                                    size={'small'}
                                    fullWidth
                                    value={part.dimensionInfo?.vendorId || ""}
                                    onFocus={() => {
                                        if (part.availableDimensions === undefined && !isLoadingDimensions) {
                                            void loadDimensionsForPart(part);
                                        }
                                    }}
                                    onChange={(e) => setDimensionForPart(part, part.availableDimensions.find(dim =>
                                        (!dim.vendorId && dim.vendorId === e.target.value) ||
                                        (dim.vendorId === Number(e.target.value))
                                    ))}
                                    disabled={isSettingDimension || isRemoving}
                                    sx={{minWidth: 100}}
                                    SelectProps={{
                                        displayEmpty: true,
                                        renderValue: () => "Select Size"
                                    }}
                                >
                                    {
                                        !isLoadingDimensions &&
                                        part.availableDimensions?.map(dim => (
                                            <MenuItem key={dim.id} value={dim.vendorId}>
                                                <Tooltip
                                                    disableInteractive
                                                    title={
                                                        <Box sx={{width: 200, display: 'flex', flexDirection: 'column'}}>
                                                            <Typography variant={'subtitle2'}>Dimensions for {dim.vendorCode ?? "General"}</Typography>
                                                            <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                                                Height: {transformLengthValue(dim.height, {fromDefault: true})}
                                                                <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/>
                                                            </Typography>
                                                            <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                                                Width: {transformLengthValue(dim.width, {fromDefault: true})}
                                                                <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/></Typography>
                                                            <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                                                Length: {transformLengthValue(dim.length, {fromDefault: true})}
                                                                <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/>
                                                            </Typography>
                                                            <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                                                Weight: {transformWeightValue(dim.weight, {fromDefault: true})}
                                                                <ProductDimensionUnit unit={weightUnit} backgroundColor={"transparent"}/>
                                                            </Typography>
                                                        </Box>
                                                    }
                                                >
                                                    <Box component={'span'}>
                                                        {dim.vendorCode ?? "General"}
                                                    </Box>
                                                </Tooltip>
                                            </MenuItem>

                                        ))
                                    }
                                    {
                                        isLoadingDimensions &&
                                        Array.from({length: 3}).map((_, index) => (
                                            <MenuItem key={`loading-${index}`} value={null} disabled>
                                                <Skeleton width={80} height={20} sx={{transform: 'none'}}/>
                                            </MenuItem>
                                        ))
                                    }
                                    {
                                        operatorAccessLevels.packages.lookup.editProductDimensions &&
                                        <MenuItem key={`management`} value={null} onClick={(e) => e.stopPropagation() || e.preventDefault()}>
                                            <Button variant={'outlined'} size={'small'} sx={{flexShrink: 0}}
                                                    onClick={(e) => e.stopPropagation() || e.preventDefault() || openDimensionManagement(part)}>
                                                Manage Sizes
                                            </Button>
                                        </MenuItem>
                                    }
                                </TextField>
                            }
                        </Box>,
                },
            })
        }) ?? [];
    }, [containers, currentContainerTypeTitle, getContainerTitle, lengthUnit, loadDimensionsForPart, loading, loadingDimensionOptions, mergedParts, openDimensionManagement, operatorAccessLevels.packages.lookup.editProductDimensions, operatorAccessLevels.packages.sales.manageSalesPallets, removeOrderDimension, removingKeys, setContainerForPart, setDimensionForPart, settingContainerKeys, settingDimensionKeys, transformLengthValue, transformWeightValue, weightUnit]);

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    Parts Info
                </Typography>
                {
                    orderParts?.some(part => part.committedQty < part.orderQty) && (
                        <Alert severity={'warning'} sx={{border: theme => `1px solid ${theme.palette.warning.main}`,}}>
                            <Typography variant={'body2'} fontWeight={600} color={'inherit'}>
                                If you see missing parts here, it is likely that you have not yet commited them. Meaning that you haven't yet selected the serial numbers of the parts or updated their
                                committed quantities in the order. Please make sure to commit all parts in order to manage their dimensions.
                            </Typography>
                        </Alert>
                    )
                }
            </Box>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns: columns,}}
                columns={dataGridColumns}
                rows={dataGridRows}
                singleSelectionOnly
                hideExporting
                hidePagination
                hidePageSize
                visibleRows={10}
                classNames={{
                    container: 'p-0 mt-0',
                    toolbar: 'p-0',
                }}
                state={{
                    pagination: {pageSize: dimensions.length, total: dimensions.length, currentPage: 1},
                    loading: {state: loading},
                    density: dataGridDensity,
                }}
            />
            <ManageProductDimensionsDialog
                {...manageProductDimensionsDialogState}
            />
        </>
    );
}